import { z } from 'zod';
import type {
  SaveInboundReceiptDraftRequest,
  SaveInboundReceiptDraftResponse,
} from './save-inbound-receipt-draft.dto.js';
import {
  CompanyReceiverCounterpartySelectionSchema,
  RegulatoryEntityCounterpartySelectionSchema,
} from '../counterparties/counterparties.schema.js';
import { IsoDateSchema, IsoDateTimeSchema } from '../shipments/shipments-types.js';
import {
  InboundReceiptDraftSchema,
  InboundReceiptPostingUnitRegistrationInputSchema,
  ReceivingTicketRowInputSchema,
} from './inbound-receipts-shared.schema.js';
import {
  DEFAULT_INBOUND_RECEIPT_CONTAINER_KIND,
  DEFAULT_INBOUND_RECEIPT_IDENTITY_MODE,
  DEFAULT_INBOUND_RECEIPT_PRODUCT_KIND,
  DEFAULT_INBOUND_RECEIPT_UNIT_TYPE,
  DEFAULT_RECEIVING_GRANULARITY,
  InboundReceiptPostingContainerKindSchema,
  InboundReceiptPostingIdentityModeSchema,
  InboundReceiptPostingProductKindSchema,
  InboundReceiptPostingUnitTypeSchema,
  ReceivingGranularitySchema,
} from './inbound-receipts-types.js';

const trimmedStringSchema = z.string().trim().min(1);
const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntegerSchema = z.int().min(0);

const baseShape = {
  companyId: z.uuid(),
  stationId: z.uuid(),
  draftId: z.uuid().optional(),
  receivingGranularity: ReceivingGranularitySchema.default(DEFAULT_RECEIVING_GRANULARITY),
  unitProfileId: z.uuid().optional(),
  unitType: InboundReceiptPostingUnitTypeSchema.default(DEFAULT_INBOUND_RECEIPT_UNIT_TYPE),
  identityMode: InboundReceiptPostingIdentityModeSchema.default(
    DEFAULT_INBOUND_RECEIPT_IDENTITY_MODE,
  ),
  containerKind: InboundReceiptPostingContainerKindSchema.default(
    DEFAULT_INBOUND_RECEIPT_CONTAINER_KIND,
  ),
  productKind: InboundReceiptPostingProductKindSchema.default(DEFAULT_INBOUND_RECEIPT_PRODUCT_KIND),
  postingUnitRegistrations: z.array(InboundReceiptPostingUnitRegistrationInputSchema).optional(),
  receiving: z
    .object({
      ticketId: z.uuid(),
      receivedAt: IsoDateTimeSchema,
      vehiclePlate: trimmedStringSchema.nullable().optional(),
      scaleLocation: trimmedStringSchema.nullable().optional(),
      rows: z.array(ReceivingTicketRowInputSchema).min(1),
      notes: trimmedStringSchema.nullable().optional(),
    })
    .strict(),
};

const SaveInternalInboundReceiptDraftSchema = z
  .object({
    ...baseShape,
    sourceType: z.literal('INTERNAL_SHIPMENT'),
    sourceShipmentCompanyId: z.uuid(),
    sourceShipmentId: z.uuid(),
    transferReference: trimmedStringSchema.nullable().optional(),
  })
  .strict();

const SaveExternalInboundReceiptDraftSchema = z
  .object({
    ...baseShape,
    sourceType: z.literal('EXTERNAL_MANUAL'),
    shipper: z.discriminatedUnion('kind', [
      CompanyReceiverCounterpartySelectionSchema,
      RegulatoryEntityCounterpartySelectionSchema,
    ]),
    materialId: z.uuid(),
    sourceDocument: z
      .object({
        shippingReportNumber: trimmedStringSchema.nullable().optional(),
        entryMode: z.enum(['SIMPLE', 'DETAILED']),
        reportFrom: IsoDateSchema,
        reportThrough: IsoDateSchema,
        shippedRedemptionWeightLb: nonNegativeNumberSchema,
        shippedRefundValueCents: nonNegativeIntegerSchema,
        ratePerLb: nonNegativeNumberSchema,
        swLb: nonNegativeNumberSchema.nullable().optional(),
        scsCount: nonNegativeIntegerSchema.nullable().optional(),
        sclCount: nonNegativeIntegerSchema.nullable().optional(),
        spLb: nonNegativeNumberSchema.nullable().optional(),
        scrapRatePerLb: nonNegativeNumberSchema.nullable().optional(),
        redemptionWeightOverridden: z.boolean().nullable().optional(),
        refundValueOverridden: z.boolean().nullable().optional(),
      })
      .strict(),
    transferReference: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const SaveInboundReceiptDraftRequestSchema: z.ZodType<SaveInboundReceiptDraftRequest> =
  z.discriminatedUnion('sourceType', [
    SaveInternalInboundReceiptDraftSchema,
    SaveExternalInboundReceiptDraftSchema,
  ]);

export const SaveInboundReceiptDraftResponseSchema: z.ZodType<SaveInboundReceiptDraftResponse> = z
  .object({
    draft: InboundReceiptDraftSchema,
  })
  .strict();
